import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { first } from 'rxjs/operators';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { BasicService } from 'src/app/shared/services/basic.service';
import { UtilsService } from 'src/app/shared/services/utils.service';

import { IpFilter, IpFilterIds, IpFilters, SessionsSettings } from '../models';
import { SessionsService } from '../sessions.service';

@Component({
  selector: 'app-sessions',
  templateUrl: './sessions.component.html',
  styleUrls: ['./sessions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionsComponent implements OnInit, OnDestroy {
  passwordNminCharactersOptions = this.utilsService.intRangeAsChoices(3, 13);
  passwordMaxFailAttemptsOptions = this.utilsService.intRangeAsChoices(3, 15);
  passwordNminSpecialCharactersOptions = this.utilsService.intRangeAsChoices(1, 13);
  passwordNoReuseNPreviousOptions = this.utilsService.intRangeAsChoices(1, 15);
  passwordMaxMonthDurationOptions = this.utilsService.intRangeAsChoices(1, 36);

  moveToPendingOptions = [
    { id: 0, label: this.basicService.translate('parameters/sessions/move-to-pending-upon-disconnect/no') },
    { id: 1, label: this.basicService.translate('parameters/sessions/move-to-pending-upon-disconnect/yes') },
  ];

  ipPlaceholderLabel = this.basicService.translate('parameters/sessions/add_ip/placeholder');

  ipFilters: IpFilter[] = [];
  ipFiltersToSave: IpFilter[] = [];
  ipFiltersToDelete: number[] = []; // just ids
  ipFilterNotSavedCurrentId = -1; // using negative id to not conflict with saved ids

  ipPattern =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

  @ViewChild('addIpFilterModalTemplateRef') addIpFilterModalTemplateRef: TemplateRef<any>;

  settingsForm = this.formBuilder.group({
    moveToPendingUponDisconnect: [],
    passwordHasLowercase: [],
    passwordHasNumber: [],
    passwordHasUppercase: [],
    passwordIsObviouslyBad: [],
    passwordMaxFailedAttempts: [],
    passwordMaxMonthDuration: [],
    passwordMaxMonthDurationCheckbox: [],
    passwordNminCharacters: [],
    passwordNminSpecialCharacters: [],
    passwordNminSpecialCharactersCheckbox: [],
    passwordNoReuseNPrevious: [],
    passwordNoReuseNPreviousCheckbox: [],
  });

  ipFilterForm = this.formBuilder.group({
    from: ['', [Validators.required, Validators.pattern(this.ipPattern)]],
    to: ['', [Validators.required, Validators.pattern(this.ipPattern)]],
  });

  get passwordNminCharactersCtrl() {
    return this.settingsForm?.get('passwordNminCharacters') as FormControl;
  }
  get passwordMaxFailedAttemptsCtrl() {
    return this.settingsForm?.get('passwordMaxFailedAttempts') as FormControl;
  }
  get passwordNminSpecialCharactersCtrl() {
    return this.settingsForm?.get('passwordNminSpecialCharacters') as FormControl;
  }
  get passwordNoReuseNPreviousCtrl() {
    return this.settingsForm?.get('passwordNoReuseNPrevious') as FormControl;
  }
  get passwordMaxMonthDurationCtrl() {
    return this.settingsForm?.get('passwordMaxMonthDuration') as FormControl;
  }
  get moveToPendingUponDisconnectCtrl() {
    return this.settingsForm?.get('moveToPendingUponDisconnect') as FormControl;
  }

  private subscriptions = new Subscription();

  constructor(
    private basicService: BasicService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private sessionsService: SessionsService,
    private utilsService: UtilsService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {}

  ngOnInit(): void {
    this.topToolbarService.setTitleTranslation('parameters/sessions/title', null);
    this.topToolbarService.setButton('save', () => {
      this.submitSettings();
    });
    this.topToolbarService.updateButtonDisable(true);

    this.sessionsService
      .getCompanySettings()
      .pipe(first())
      .subscribe((result: SessionsSettings) => {
        this.patchForm(result);
        this.checkboxOnValueChanges('passwordMaxMonthDuration', 'passwordMaxMonthDurationCheckbox');
        this.checkboxOnValueChanges('passwordNminSpecialCharacters', 'passwordNminSpecialCharactersCheckbox');
        this.checkboxOnValueChanges('passwordNoReuseNPrevious', 'passwordNoReuseNPreviousCheckbox');

        this.subscriptions.add(
          this.settingsForm.valueChanges.subscribe(() => {
            this.topToolbarService.updateButtonDisable(false);
          })
        );
      });

    this.sessionsService.getIpFilters().subscribe((result: IpFilters) => {
      this.ipFilters = result.ip_filters;
    });
  }

  /**
   * Set the value of formControlName to 1 or 0, depending if checkboxFormControlName is checked or not
   * and disable formControlName if value = 0
   */
  private checkboxOnValueChanges = (formControlName: string, checkboxFormControlName: string) => {
    const control = this.settingsForm.controls[formControlName];
    const checkbox = this.settingsForm.controls[checkboxFormControlName];

    this.subscriptions.add(
      checkbox.valueChanges.subscribe(checked => {
        control.setValue(+checked); // unary plus operator
        checked ? control.enable() : control.disable();
      })
    );
  };

  /**
   * Disable formControlName if value = 0
   */
  private disableControl = (value: number, formControlName: string) => {
    const control = this.settingsForm.controls[formControlName];
    value ? control.enable() : control.disable();
  };

  /**
   * Fill settingsForm
   */
  private patchForm(sessionsSettings: SessionsSettings) {
    const {
      move_to_pending_upon_disconnect,
      password_has_lowercase,
      password_has_number,
      password_has_uppercase,
      password_is_obviously_bad,
      password_max_failed_attempts,
      password_max_month_duration,
      password_nmin_characters,
      password_nmin_special_characters,
      password_no_reuse_n_previous,
    } = sessionsSettings;

    this.settingsForm.patchValue({
      moveToPendingUponDisconnect: move_to_pending_upon_disconnect,
      passwordHasLowercase: password_has_lowercase,
      passwordHasNumber: password_has_number,
      passwordHasUppercase: password_has_uppercase,
      passwordIsObviouslyBad: password_is_obviously_bad,
      passwordMaxFailedAttempts: password_max_failed_attempts,
      passwordMaxMonthDuration: password_max_month_duration,
      passwordMaxMonthDurationCheckbox: !!password_max_month_duration,
      passwordNminCharacters: password_nmin_characters,
      passwordNminSpecialCharacters: password_nmin_special_characters,
      passwordNminSpecialCharactersCheckbox: !!password_nmin_special_characters,
      passwordNoReuseNPrevious: password_no_reuse_n_previous,
      passwordNoReuseNPreviousCheckbox: !!password_no_reuse_n_previous,
    });

    this.disableControl(password_max_month_duration, 'passwordMaxMonthDuration');
    this.disableControl(password_nmin_special_characters, 'passwordNminSpecialCharacters');
    this.disableControl(password_no_reuse_n_previous, 'passwordNoReuseNPrevious');
  }

  /**
   * Save modifications if there are any
   */
  submitSettings() {
    if (this.settingsForm.dirty) {
      const {
        moveToPendingUponDisconnect,
        passwordHasLowercase,
        passwordHasNumber,
        passwordHasUppercase,
        passwordIsObviouslyBad,
        passwordMaxFailedAttempts,
        passwordMaxMonthDuration,
        passwordNminCharacters,
        passwordNminSpecialCharacters,
        passwordNoReuseNPrevious,
      } = this.settingsForm.getRawValue();

      const sessionsSettings: SessionsSettings = {
        move_to_pending_upon_disconnect: moveToPendingUponDisconnect,
        password_nmin_characters: passwordNminCharacters,
        password_nmin_special_characters: passwordNminSpecialCharacters,
        password_max_failed_attempts: passwordMaxFailedAttempts,
        password_no_reuse_n_previous: passwordNoReuseNPrevious,
        password_max_month_duration: passwordMaxMonthDuration,
        password_is_obviously_bad: passwordIsObviouslyBad,
        password_has_lowercase: passwordHasLowercase,
        password_has_uppercase: passwordHasUppercase,
        password_has_number: passwordHasNumber,
      };

      this.sessionsService.updateCompanySettings(sessionsSettings).subscribe(() => {
        this.settingsForm.markAsPristine();
        this.topToolbarService.updateButtonDisable(true);
      });
    }

    if (this.ipFiltersToSave.length) {
      const ipFilters: IpFilters = {
        ip_filters: this.ipFiltersToSave,
      };
      this.sessionsService.addIpFilter(ipFilters).subscribe((result: IpFilters) => {
        this.ipFilters = result.ip_filters;
        this.ipFiltersToSave = [];
        this.topToolbarService.updateButtonDisable(true);
      });
    }

    if (this.ipFiltersToDelete.length) {
      const ipFilterIds: IpFilterIds = {
        ip_filter_ids: this.ipFiltersToDelete,
      };
      this.sessionsService.deleteIpFilters(ipFilterIds).subscribe((result: IpFilters) => {
        this.ipFilters = result.ip_filters;
        this.ipFiltersToDelete = [];
        this.topToolbarService.updateButtonDisable(true);
      });
    }
  }

  /**
   * Open modal to add an ipFilter
   */
  addIpFilter() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'app-sessions',
      data: {
        title: this.basicService.translate('parameters/sessions/add_ip_range'),
        templateRef: this.addIpFilterModalTemplateRef,
        disable: () => this.ipFilterForm.invalid,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('save'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const { from, to } = this.ipFilterForm.value;
        const newIpFilter: IpFilter = { from, to };

        this.ipFiltersToSave.push({ ...newIpFilter }); // no "id" (following line) in ipFiltersToSave

        newIpFilter.id = this.ipFilterNotSavedCurrentId--; // using negative id to not conflict with saved ids
        this.ipFilters.push(newIpFilter);

        this.topToolbarService.updateButtonDisable(false);
      }
      this.ipFilterForm.reset();
    });
  }

  /**
   * Remove one ip filter
   */
  deleteIpFilter(ipFilter: IpFilter) {
    this.ipFiltersToDelete.push(ipFilter.id);
    this.ipFilters = this.ipFilters.filter(ipf => ipf.id !== ipFilter.id);
    this.topToolbarService.updateButtonDisable(false);
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.topToolbarService.clearButtons();
  }
}
